import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  providerRateLimitsApi,
  type ProviderRateLimitListResponse,
  type ProviderRateLimitConfig,
  type UpdateProviderRateLimitRequest,
} from '../api/services/provider-rate-limits';

const QUERY_KEY = ['admin', 'provider-rate-limits'];

/**
 * Fetch all provider rate limit configurations
 */
export function useProviderRateLimits(tenantId?: string) {
  return useQuery<ProviderRateLimitListResponse>({
    queryKey: [...QUERY_KEY, tenantId],
    queryFn: () => providerRateLimitsApi.list(tenantId),
    refetchInterval: 30_000, // Refresh every 30s to show live quota
  });
}

/**
 * Fetch rate limit config for a single provider
 */
export function useProviderRateLimit(provider: string) {
  return useQuery<ProviderRateLimitConfig>({
    queryKey: [...QUERY_KEY, provider],
    queryFn: () => providerRateLimitsApi.getProvider(provider),
    enabled: !!provider,
  });
}

/**
 * Update platform-level rate limits for a provider
 */
export function useUpdateProviderRateLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      provider,
      data,
    }: {
      provider: string;
      data: UpdateProviderRateLimitRequest;
    }) => providerRateLimitsApi.updateProvider(provider, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Reset rate limit counters for a provider
 */
export function useResetProviderCounters() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider: string) => providerRateLimitsApi.resetCounters(provider),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
